import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MosaicPage from './interaction_pages/MosaicPage';
import OneChoicePage from './interaction_pages/OneChoicePage';
import ContextPage from './explanation_pages/ContextPage';
import { normalizeContextContent } from './explanation_pages/contextLayouts';
import ScoreBoard from './graph_pages/ScoreBoard';
import CommandControlPage from './interaction_pages/CommandControlPage';
import CustomPage from './graph_pages/CustomPage';
import TwoDoughnutsGraphs from './graph_pages/TwoDoughnutsGraphs';
import TerritoryIntroPage from './territory_intro/TerritoryIntroPage';
import { TerritoryStageProvider, useStageView } from './terrain3d/TerritoryStage';
import ErrorBoundary from './other_components/ErrorBoundary';
import { isTerminalPage, nextLabel } from './gameFlow';

/**
 * CE QUE LA PAGE COURANTE VEUT DE LA CARTE — pour tous les types de page SAUF
 * le module territoire, qui pilote l'écran étape par étape et se déclare
 * lui-même.
 *
 * POURQUOI ICI PLUTÔT QUE DANS CHAQUE PAGE. Poser une carte en fond d'une
 * mosaïque, d'un tableau de bord ou d'une page de contexte est le même geste,
 * et il ne regarde en rien ce que ces pages savent faire. Un seul déclarant
 * évite d'apprendre l'existence du territoire à quatre composants sans lien.
 *
 * ET POURQUOI UN COMPOSANT À PART : `GamePage` FOURNIT le contexte de l'écran,
 * or on ne peut pas consommer, dans le même rendu, ce qu'on vient de fournir.
 *
 * Le territoire et ses couches viennent de la SÉANCE (`game_status.territory`),
 * pas de la page : une page ne choisit pas ce qui est chargé, elle choisit ce
 * qu'elle montre.
 */
// Identifiant du repère d'une page localisée. Constant : il n'y en a qu'un par
// page, et rien ne le référence ailleurs.
const ANCHOR_POINT_ID = 'page-anchor';

/**
 * Le repère d'une page localisée, construit depuis LE CONTENU DE LA PAGE.
 *
 * La une de journal n'est donc écrite qu'à un seul endroit : `map_view.anchor`
 * ne porte que le lieu, jamais ce qui s'y passe. C'est ce qui permet de
 * localiser une page de contexte sans dupliquer ses champs — et ce qui aurait
 * été perdu en créant un type de page distinct.
 */
function anchorPoint(pageData) {
  const anchor = pageData?.map_view?.anchor;
  if (!anchor?.position) return null;
  // Le contenu est lu au format canonique : le repère montre ce que la page
  // montre, y compris pour les pages écrites avant les formats.
  const content = normalizeContextContent(pageData.content);
  return {
    point_id: ANCHOR_POINT_ID,
    position: anchor.position,
    title: pageData.title || anchor.label || '',
    // La fiche affiche une IMAGE : une page dont le média est une vidéo ne peut
    // pas la montrer ici, elle garde son texte et son lieu.
    image_url: content.media_type === 'video' ? '' : content.media_url,
    text: content.text,
  };
}

/**
 * Ce qu'une page demande à la carte, résolu contre le territoire de la séance.
 * Rend `null` si la page ne demande rien, ou si la séance n'a pas de carte.
 *
 * Calculé dans `GamePage` plutôt que dans le déclarant ci-dessous, parce que
 * l'affichage en dépend AUSSI : une page localisée ne rend pas sa mise en page
 * habituelle, c'est la carte qui la porte.
 */
export function resolveMapView(pageData, territory) {
  const view = pageData?.map_view;
  if (!view || !view.mode || view.mode === 'off') return null;
  if (pageData.type === 'territory_intro' || !territory?.id) return null;

  // UN LIEU APPARTIENT À UN TERRITOIRE — cadrage comme ancre. Ils sont exprimés
  // en coordonnées du terrain : ce qui désigne la vallée de l'Eyrieux sur
  // l'Ardèche ne désigne rien ailleurs. Une page étant partagée entre ateliers,
  // qui peuvent porter des territoires différents, on ignore alors le repérage
  // plutôt que de poser un marqueur dans le vide.
  const sameTerritory = !view.territory_id || view.territory_id === territory.id;
  const point = sameTerritory ? anchorPoint(pageData) : null;

  // Une page localisée sans ancre applicable n'a plus rien à situer : elle
  // reprend sa mise en page habituelle plutôt que d'afficher une carte muette.
  if (view.mode === 'located' && !point) return null;

  return {
    territory_id: territory.id,
    layers: territory.layers || [],
    mode: view.mode,
    active_layers: view.active_layers || [],
    layer_settings: view.layer_settings || {},
    camera: sameTerritory ? (view.camera || null) : null,
    motion: view.motion || null,
    treatment: view.treatment || null,
    fog_enabled: view.fog_enabled !== false,
    points: point ? [point] : (view.points_of_interest || []),
    open_point_id: point ? ANCHOR_POINT_ID : null,
    content_insets: view.content_insets || null,
  };
}

const PageStageView = ({ pageData, view }) => {
  useStageView(pageData?._id || 'page', view || null);
  return null;
};

/**
 * Les types de page que `GameBody` sait monter. Sert au repli ci-dessous, et
 * uniquement à lui : la liste des composants reste écrite une seule fois, dans
 * le JSX. Doit rester d'accord avec KNOWN_PAGE_TYPES (api/game_validator.py),
 * qui avertit l'auteur du même défaut avant qu'il n'atteigne une salle.
 */
const RENDERED_TYPES = new Set([
  'mosaic', 'one-choice', 'context', 'score', 'command_control',
  'two_doughnuts_graphs', 'custom', 'territory_intro',
]);

/**
 * LE RENDU D'UNE PAGE DE JEU, en un seul exemplaire.
 *
 * La fin de partie n'est pas un autre écran : c'est la même page, sans le
 * bouton qui fait avancer. Ce composant existe pour que ce soit vrai —
 * auparavant `isFinish` ouvrait un second moteur de rendu, avec sa propre liste
 * de types acceptés, et tout atelier finissant sur un type absent de cette
 * liste tombait sur une page blanche.
 *
 * `displayButton` est le seul levier du mode terminal. Il vaut `true` partout
 * ailleurs : aucun appel existant ne change. `endsGame` ne fait que NOMMER le
 * bouton (cf. gameFlow.js) : il n'ouvre aucun chemin, et le confondre avec
 * `displayButton` serait une faute — l'un décrit la dernière page jouable,
 * l'autre la partie déjà finie.
 */
const GameBody = ({
  pageData,
  gameStatus,
  gameId,
  onDecisionSelect,
  isLocated = false,
  displayButton = true,
  endsGame = false,
}) => (
  <div>
    {pageData.type === 'mosaic' && (
      <MosaicPage
        pageData={pageData}
        gameStatus={gameStatus}
        gameId={gameId}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
      />
    )}
    {pageData.type === 'one-choice' && (
      <OneChoicePage
        pageData={pageData}
        gameStatus={gameStatus}
        gameId={gameId}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
      />
    )}
    {/* PAGE LOCALISÉE : la carte porte l'écran. Le contenu n'est pas rendu
        ici — il est déjà dans la fiche reliée au marqueur (cf.
        `anchorPoint`), et le redoubler afficherait la une deux fois. Ne
        reste que ce que la carte n'a pas : la suite du jeu. */}
    {pageData.type === 'context' && isLocated && displayButton && (
      <div className="located-page-nav">
        <button
          className="blue-button"
          onClick={() => onDecisionSelect({ page_id: pageData._id, page_type: 'context' })}
        >
          {nextLabel(endsGame)}
        </button>
      </div>
    )}
    {pageData.type === 'context' && !isLocated && (
      <ContextPage
        pageData={pageData}
        gameStatus={gameStatus}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
        endsGame={endsGame}
      />
    )}
    {pageData.type === 'score' && (
      <ScoreBoard
        gameId={gameId}
        pageData={pageData}
        gameStatus={gameStatus}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
        endsGame={endsGame}
      />
    )}
    {pageData.type === 'command_control' && (
      <CommandControlPage
        pageData={pageData}
        gameStatus={gameStatus}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
      />
    )}
    {pageData.type === 'two_doughnuts_graphs' && (
      <TwoDoughnutsGraphs
        pageData={pageData}
        gameStatus={gameStatus}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
        endsGame={endsGame}
      />
    )}
    {/* UN ÉCRAN CUSTOM EST UNE PAGE DE PILE, donc il fait avancer comme les
        autres. Il n'avait ni bouton ni `onDecisionSelect` du temps où les
        bilans vivaient hors pile : une partie posée sur un tel écran n'aurait
        eu aucun moyen d'en sortir, ni donc de se terminer. */}
    {pageData.type === 'custom' && (
      <CustomPage
        pageData={pageData}
        gameStatus={gameStatus}
        onDecisionSelect={onDecisionSelect}
        displayButton={displayButton}
        endsGame={endsGame}
      />
    )}
    {pageData.type === 'territory_intro' && (
      <ErrorBoundary
        fallback={
          <div className="page-general-container">
            <div className="page-main-content">
              <p>Un problème est survenu pendant le chargement de la carte.</p>
              {displayButton && (
                <button
                  className="blue-button"
                  onClick={() => onDecisionSelect({ page_id: pageData._id, page_type: 'territory_intro' })}
                >
                  Continuer
                </button>
              )}
            </div>
          </div>
        }
      >
        <TerritoryIntroPage
          pageData={pageData}
          gameStatus={gameStatus}
          onDecisionSelect={onDecisionSelect}
          displayButton={displayButton}
          endsGame={endsGame}
        />
      </ErrorBoundary>
    )}
    {/* TYPE INCONNU : le repli est VISIBLE et il fait avancer. Une suite de
        `&&` sans branche par défaut rendait un `<div>` vide — l'écran blanc
        sans issue, la panne même que la refonte de la fin de partie a
        supprimée ailleurs. Un type disparaît du routeur pour des raisons
        ordinaires (page écrite par un builder plus récent, type retiré, faute
        de frappe), et ça ne doit pas bloquer une salle. */}
    {!RENDERED_TYPES.has(pageData.type) && (
      <div className="page-general-container">
        <div className="page-general-background-color"></div>
        <div className="page-main-content">
          <div className="custom-page-missing">
            <h2>Page non affichable</h2>
            <p>
              La page <code>{pageData._id}</code> est de type{' '}
              <code>{pageData.type || '(aucun)'}</code>, qu'aucun composant ne sait monter.
            </p>
            {displayButton && (
              <button
                className="blue-button"
                onClick={() => onDecisionSelect({ page_id: pageData._id, page_type: pageData.type })}
              >
                {nextLabel(endsGame)}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
);

/**
 * LA SORTIE DE FIN DE PARTIE.
 *
 * Posée par-dessus la page, en position fixe : la fin d'un atelier doit avoir
 * une sortie quoi qu'il y ait derrière — une carte de territoire qui occupe
 * tout l'écran, ou rien du tout si la page finale est introuvable. C'est la
 * raison pour laquelle elle est rendue HORS du conditionnel sur `pageData`.
 *
 * ELLE RECOUVRE, DONC ELLE DÉGAGE. Le défilement d'une page de jeu vit DANS
 * `.page-general-container` (hauteur d'écran, `overflow-y: auto`), pas sur la
 * fenêtre : une barre fixe masque donc le bas du contenu de façon définitive,
 * sans qu'aucun défilement puisse l'atteindre. C'est pourquoi le bloc de fin
 * porte `.game-finished`, qui rend au contenu la hauteur que la barre lui
 * prend. Réduire la barre ne suffisait pas — ça n'aurait fait que raccourcir
 * ce qui reste inatteignable.
 *
 * `/profile` est derrière ProtectedRoute : si la séance a duré plus longtemps
 * que le jeton, ce bouton mène à l'écran de connexion. C'est le comportement
 * normal de l'application, et il vaut toujours mieux qu'un écran sans issue.
 */
const GameEndBar = () => {
  const navigate = useNavigate();
  return (
    <div className="game-end-bar">
      <span className="game-end-bar-label">Atelier terminé</span>
      <button className="blue-button" onClick={() => navigate('/profile')}>
        Retour à l'accueil
      </button>
    </div>
  );
};

const GamePage = ({ gameId }) => {
  const [pageData, setPageData] = useState(null);
  const [gameStatus, setGameStatus] = useState(null);
  const [isFinish, setIsFinish] = useState(false);
  // Requête en cours. À NE PAS CONFONDRE avec « rien à montrer » : entre deux
  // pages, on a déjà quelque chose à l'écran, et c'est tout l'objet du
  // changement ci-dessous.
  const [isPending, setIsPending] = useState(true);
  // Jeton d'arrivée de page. Il sert de `key` au bloc de jeu, donc il REMONTE
  // le composant de page à chaque transition — ce que faisait auparavant, par
  // accident, le passage par l'écran de chargement. Sans lui, deux mosaïques
  // qui se suivent réutiliseraient la même instance de `MosaicPage`, et les
  // décisions cochées sur la première réapparaîtraient sur la seconde.
  //
  // Un compteur et non `pageData._id` : une pile peut enchaîner deux fois la
  // même page (un tableau de scores entre deux rounds), et la clé ne changerait
  // pas.
  const [pageToken, setPageToken] = useState(0);
  // La dernière requête a échoué. NÉCESSAIRE, ET PAS COSMÉTIQUE : sans lui, un
  // `catch` qui ne fait qu'écrire dans la console laisse `pageData` à null et
  // `isFinish` à false, donc l'écran de chargement — pour toujours, sans
  // bouton. C'était la dernière porte ouverte sur un écran sans issue, celle
  // que la refonte de la fin de partie n'avait pas fermée.
  const [loadError, setLoadError] = useState(null);

  // Fonction pour récupérer la page de jeu
  //
  // LA PAGE COURANTE RESTE AFFICHÉE JUSQU'À L'ARRIVÉE DE LA SUIVANTE. Elle
  // était auparavant remplacée par l'écran de chargement dès le début de la
  // requête : l'arbre entier était démonté à chaque transition, y compris tout
  // ce qui doit accompagner la séance — au premier rang la carte du territoire,
  // dont le fond aurait clignoté entre chaque page. Le voile de transition se
  // pose donc PAR-DESSUS, il ne remplace rien.
  const fetchPageData = async () => {
    setIsPending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_current_page/${gameId}`);
      if (!response.ok) throw new Error(`Réponse ${response.status} du serveur.`);
      const data = await response.json();
      // `content_found: false` = le serveur a la partie mais n'a pas su
      // composer la page. Il n'était pas lu : la partie s'arrêtait alors sur
      // l'écran de chargement, sans que rien ne le dise.
      if (!data.is_finish && !data.page_content) {
        throw new Error("La page suivante n'a pas pu être composée par le serveur.");
      }
      setPageData(data.page_content);
      setGameStatus(data.game_status);
      setIsFinish(data.is_finish);
      setPageToken((token) => token + 1);
      setLoadError(null);
    } catch (error) {
      console.error('Error fetching page data:', error);
      setLoadError(error.message || 'Erreur inconnue.');
    } finally {
      setIsPending(false);
    }
  };

  // Charger les données au montage initial
  //
  // IL N'Y A PLUS DE SECONDE REQUÊTE POUR LA PAGE DE FIN. `/get_current_page`
  // la rend lui-même quand la pile est vide (cf. game_functions._final_page),
  // complète et donc utilisable telle quelle — ce que `/get_page` ne savait pas
  // faire, faute de résoudre les décisions.
  useEffect(() => {
    fetchPageData();
  }, [gameId]);

  // Gérer la sélection d'une décision
  const handleDecisionSelect = async (pageResult) => {
    if (!pageResult) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/post_page_result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pageResult, game_id: gameId }),
      });
      if (!response.ok) throw new Error(`Réponse ${response.status} du serveur.`);
      const result = await response.json();
      console.log('Result sent to backend:', result);

      // Rafraîchir les données après le post
      await fetchPageData();
    } catch (error) {
      console.error('Error posting page result:', error);
      // Le clic n'a rien produit : le dire. En salle, un bouton qui ne répond
      // pas fait recliquer, et un second envoi validerait deux pages.
      setLoadError(error.message || 'Erreur inconnue.');
      setIsPending(false);
    }
  };

  // Premier chargement : il n'y a littéralement rien à montrer. C'est le SEUL
  // cas où l'écran de chargement remplace la partie ; toutes les transitions
  // suivantes se contentent du voile posé plus bas.
  const nothingToShow = !pageData && !isFinish;

  // Valider cette page-ci termine-t-il la séance ? Ne sert qu'à nommer le
  // bouton (cf. gameFlow.js).
  const endsGame = isTerminalPage(pageData, gameStatus);

  // Ce que la page courante demande à la carte, et si elle s'en remet à elle
  // pour tout l'écran (mode `located`).
  const mapView = resolveMapView(pageData, gameStatus?.territory);
  const isLocated = mapView?.mode === 'located';

  // L'ÉCRAN DE TERRITOIRE ENVELOPPE LA PARTIE ENTIÈRE, écran de chargement
  // compris — et ce détail est ce qui fait tenir tout le reste : le provider ne
  // doit jamais être démonté au fil des pages, sinon la scène 3D est disposée
  // puis reconstruite et la carte n'a rien gagné à devenir un objet de séance.
  //
  // Un atelier sans territoire ne paie rien : `territory` est alors absent de
  // `game_status`, le viewer n'est pas monté et aucun fichier de /terrain3d/
  // n'est demandé.
  return (
    <TerritoryStageProvider territory={gameStatus?.territory}>
      {/* Hors du bloc porteur de `key` : la vue de la carte doit se METTRE À
          JOUR d'une page à l'autre, pas se relâcher puis se reprendre. */}
      <PageStageView pageData={pageData} view={mapView} />

      {nothingToShow && loadError ? (
        /* RIEN À MONTRER ET UNE REQUÊTE EN ÉCHEC. Auparavant : l'écran de
           chargement, indéfiniment. La séance n'est pas perdue — elle est en
           base, à sa page — donc l'écran le dit et propose de reprendre. */
        <div className="page-general-container">
          <div className="page-general-background-color"></div>
          <div className="page-general-background-hero" style={{ backgroundImage: `url(/img/bgrille-m.svg)` }}></div>
          <div className="page-main-content">
            <div className="custom-page-missing">
              <h2>La séance n'a pas pu être chargée</h2>
              <p>{loadError}</p>
              <p>La partie est enregistrée : reprendre la charge à la page en cours.</p>
              <button className="blue-button" onClick={fetchPageData}>Réessayer</button>
            </div>
          </div>
        </div>
      ) : nothingToShow ? (
        <div className="page-general-container">
          <div className='page-general-background-color'></div>

          <div className='page-general-background-hero' style={{ backgroundImage: `url(/img/bgrille-m.svg)` }}>
              {/* <div className='page-general-background-image' style={{ backgroundImage: `url(${pageData.content.background_image_url})` }}></div> */}
          </div>
          <div className='page-main-content'>
            <img src='/img/loading.gif' alt='Loading...' className='loading-icon'/>
          </div>
        </div>
      ) : isFinish ? (
      /* FIN DE PARTIE : la même page que pendant le jeu, sans le bouton qui
         fait avancer, plus la sortie. Aucun test sur le type ni sur
         l'identifiant de la page finale — c'est ce qui garantit qu'un atelier
         ne peut plus se terminer sur un écran vide, quel que soit ce sur quoi
         son auteur l'a fait finir.

         `onDecisionSelect` est neutralisé plutôt que retiré : un composant qui
         l'appellerait quand même (un chemin oublié, un clavier) ne doit pas
         relancer une partie terminée. */
      <div className="game-finished">
        {pageData ? (
          <GameBody
            pageData={pageData}
            gameStatus={gameStatus}
            gameId={gameId}
            onDecisionSelect={() => {}}
            isLocated={isLocated}
            displayButton={false}
          />
        ) : (
          /* Aucune page de fin résoluble : la partie n'a aucune page jouée
             (et, pour une séance d'avant la bascule, aucun `last_page`
             valide). Le décor habituel plutôt qu'un fond blanc — c'est encore
             un écran de l'atelier, pas une erreur du navigateur. */
          <div className="page-general-container">
            <div className="page-general-background-color"></div>
            <div className="page-general-background-hero" style={{ backgroundImage: `url(/img/bgrille-m.svg)` }}></div>
            <div className="page-main-content">
              <div className="end-page-header">Fin de l'atelier</div>
            </div>
          </div>
        )}
        <GameEndBar />
      </div>
    ) : (
      /* `key` : voir `pageToken` — c'est lui qui garantit qu'une page repart
         d'un état neuf, maintenant que l'écran de chargement ne la démonte
         plus au passage. */
      <GameBody
        key={pageToken}
        pageData={pageData}
        gameStatus={gameStatus}
        gameId={gameId}
        onDecisionSelect={handleDecisionSelect}
        isLocated={isLocated}
        endsGame={endsGame}
      />
      )}

      {/* UNE REQUÊTE A ÉCHOUÉ ALORS QU'UNE PAGE EST À L'ÉCRAN. La page reste,
          l'échec se dit : sans ce bandeau, le clic sur « Suivant » n'aurait
          produit aucun changement visible, et le réflexe en salle est de
          recliquer — donc de valider deux fois la même page. */}
      {loadError && !nothingToShow && (
        <div className="game-load-error" role="alert">
          <span>{loadError}</span>
          <button className="blue-button" onClick={fetchPageData}>Réessayer</button>
        </div>
      )}

      {/* VOILE DE TRANSITION. Il remplace l'écran de chargement entre deux
          pages : le clic sur « Suivant » doit répondre tout de suite, mais sans
          effacer ce qui est à l'écran. Il ne se montre pas au tout premier
          chargement, où la page pleine ci-dessus fait déjà le travail. */}
      {isPending && !nothingToShow && (
        <div className="page-transition-veil" aria-hidden="true">
          <img src="/img/loading.gif" alt="" />
        </div>
      )}
    </TerritoryStageProvider>
  );
};

export default GamePage;
